/**
 * Pricing Input Materials (Insumos) Service
 * Manages input materials (insumos) for the pricing context
 */

import { supabase } from '@/lib/supabase'

export interface PricingInput {
  id: string
  unit_id: string
  name: string
  uom: string
  price?: number
  category_id?: string
  group_id?: string
  active: boolean
  attrs?: Record<string, any>
  created_at?: string
  updated_at?: string
}

export interface CreatePricingInputInput {
  name: string
  uom: string
  price?: number
}

export interface UpdatePricingInputInput {
  name?: string
  uom?: string
  price?: number
  active?: boolean
  attrs?: Record<string, any>
}

/**
 * Get the "Insumo" group ID for the pricing context
 * This group is used to categorize all input materials
 */
interface PricingInputGroup {
  id: string
  category_id: string | null
}

async function getPricingInputGroup(): Promise<PricingInputGroup> {
  try {
    // First get the pricing context ID
    const { data: contextData, error: contextError } = await supabase
      .from('contexts')
      .select('id')
      .eq('key', 'pricing')
      .single()

    if (contextError) {
      console.error('Error fetching pricing context:', contextError)
      throw contextError
    }

    console.log('[getPricingInputGroup] Pricing context ID:', contextData.id)

    // Fetch the input_materials group — also select category_id to avoid
    // a separate query against asset_categories (which may be RLS-restricted)
    const { data: groupData, error: groupError } = await supabase
      .from('asset_groups')
      .select('id, key, name, context_id, category_id')
      .eq('context_id', contextData.id)
      .eq('key', 'input_materials')
      .maybeSingle()

    console.log('[getPricingInputGroup] Group query result:', { groupData, groupError })

    if (groupError) {
      console.error('Error fetching pricing input group:', groupError)
      throw groupError
    }

    if (!groupData) {
      throw new Error(
        'Grupo "Insumos" (input_materials) não encontrado no contexto de precificação. ' +
        'Verifique as políticas RLS ou execute a migration 070 no banco de dados.'
      )
    }

    console.log('[getPricingInputGroup] Found group:', groupData.id, 'category_id:', groupData.category_id)
    return { id: groupData.id, category_id: groupData.category_id }
  } catch (error) {
    console.error('Error in getPricingInputGroup:', error)
    throw error
  }
}



// ─── Component Candidates (insumos reais + templates da atividade) ──────────

export interface ComponentCandidate {
  id: string
  name: string
  uom: string
  price?: number
  source_type: 'asset' | 'template'
  template_id?: string
}

/**
 * Lista insumos reais da unidade + templates não materializados da atividade.
 * Usa o RPC get_component_candidates (migração 073).
 */
export async function listComponentCandidates(
  unitId: string,
  search?: string
): Promise<ComponentCandidate[]> {
  const { data, error } = await supabase.rpc('get_component_candidates', {
    p_unit_id: unitId,
    p_search: search ?? null,
    p_limit: 100
  })

  if (error) throw error
  return (data || []) as ComponentCandidate[]
}

/**
 * List all active input materials for a unit
 */
export async function listPricingInputs(unitId: string): Promise<PricingInput[]> {
  try {
    const group = await getPricingInputGroup()
    const { data, error } = await supabase
      .from('assets')
      .select(`
        id,
        unit_id,
        name,
        uom,
        price,
        category_id,
        group_id,
        active,
        attrs,
        created_at,
        updated_at
      `)
      .eq('unit_id', unitId)
      .eq('active', true)
      // Filter by pricing context inputs group
      .eq('group_id', group.id)
      .order('name', { ascending: true })

    if (error) throw error
    return (data || []) as PricingInput[]
  } catch (error) {
    console.error('Error listing pricing inputs:', error)
    throw error
  }
}

/**
 * Get a single input by ID
 */
export async function getPricingInput(id: string): Promise<PricingInput> {
  try {
    const { data, error } = await supabase
      .from('assets')
      .select(`
        id,
        unit_id,
        name,
        uom,
        price,
        category_id,
        group_id,
        active,
        attrs,
        created_at,
        updated_at
      `)
      .eq('id', id)
      .single()

    if (error) throw error
    return data as PricingInput
  } catch (error) {
    console.error('Error fetching pricing input:', error)
    throw error
  }
}

/**
 * Create a new input material
 * Automatically sets:
 * - category_id: "Insumo" category
 * - group_id: "input_materials" group for pricing context
 */
export async function createPricingInput(
  unitId: string,
  input: CreatePricingInputInput
): Promise<PricingInput> {
  try {
    const group = await getPricingInputGroup()

    const { data, error } = await supabase
      .from('assets')
      .insert([
        {
          unit_id: unitId,
          name: input.name,
          uom: input.uom,
          price: input.price || null,
          category_id: group.category_id,
          group_id: group.id,
          active: true,
          attrs: {}
        }
      ])
      .select(
        `
        id,
        unit_id,
        name,
        uom,
        price,
        category_id,
        group_id,
        active,
        attrs,
        created_at,
        updated_at
      `
      )
      .single()

    if (error) throw error
    return data as PricingInput
  } catch (error) {
    console.error('Error creating pricing input:', error)
    throw error
  }
}

/**
 * Update an input material
 */
export async function updatePricingInput(
  id: string,
  changes: UpdatePricingInputInput
): Promise<PricingInput> {
  try {
    const { data, error } = await supabase
      .from('assets')
      .update(changes)
      .eq('id', id)
      .select(
        `
        id,
        unit_id,
        name,
        uom,
        price,
        category_id,
        group_id,
        active,
        attrs,
        created_at,
        updated_at
      `
      )
      .single()

    if (error) throw error
    return data as PricingInput
  } catch (error) {
    console.error('Error updating pricing input:', error)
    throw error
  }
}

/**
 * Soft delete (deactivate) an input material
 */
export async function deactivatePricingInput(id: string): Promise<void> {
  try {
    const { error } = await supabase
      .from('assets')
      .update({ active: false })
      .eq('id', id)

    if (error) throw error
  } catch (error) {
    console.error('Error deactivating pricing input:', error)
    throw error
  }
}

/**
 * Hard delete an input material
 */
export async function deletePricingInput(id: string): Promise<void> {
  try {
    const { error } = await supabase
      .from('assets')
      .delete()
      .eq('id', id)

    if (error) throw error
  } catch (error) {
    console.error('Error deleting pricing input:', error)
    throw error
  }
}
